import React, { useState, useEffect, useRef } from 'react';
import { crearActividadExtracurricular, actualizarActividadExtracurricular } from '../../services/ActividadesService';
import { obtenerTodasLasMatriculas } from '../../services/matriculaService';
import { FaCalendarAlt, FaFile, FaTrash, FaUpload, FaSearch } from 'react-icons/fa';
import { X } from 'lucide-react';

const ModalActividadExtracurricular = ({ actividad, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        titulo: '',
        descripcion: '',
        id_child: [],
        fecha_limite: '',
        archivo: null
    });
    const [archivoExistente, setArchivoExistente] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [ninos, setNinos] = useState([]);
    const [ninosFiltrados, setNinosFiltrados] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [tituloError, setTituloError] = useState('');
    const [participantesError, setParticipantesError] = useState('');
    const [fechaError, setFechaError] = useState('');
    const [nombreArchivo, setNombreArchivo] = useState('');
    const [archivoError, setArchivoError] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const selectRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setShowOptions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        cargarNinos();
        if (actividad && !formData.titulo) {
            setFormData({
                titulo: actividad.titulo || '',
                descripcion: actividad.descripcion || '',
                id_child: [actividad.id_child],
                fecha_limite: actividad.fecha_limite ? new Date(actividad.fecha_limite).toISOString().split('T')[0] : '',
                archivo: null
            });
            if (actividad.archivo) {
                setArchivoExistente(actividad.archivo);
                setNombreArchivo(actividad.archivo.split('/').pop());
            }
        }
    }, [actividad]);

    useEffect(() => {
        if (formData.id_child.length > 0 && ninos.length > 0) {
            const ninoSeleccionado = ninos.find(n => n.id_child === formData.id_child[0]);
            if (ninoSeleccionado && !busqueda) {
                setBusqueda(ninoSeleccionado.nombre);
            }
        }
    }, [formData.id_child, ninos]);

    const cargarNinos = async () => {
        try {
            const response = await obtenerTodasLasMatriculas();
            // Filtrar solo niños con matrícula activa
            const ninosActivos = response.matriculas
                .filter(matricula => matricula.matricula && matricula.matricula.estado === 'Activa')
                .map(matricula => ({
                id_child: matricula.nino.id_child,
                nombre: `${matricula.nino.nombre} ${matricula.nino.apellidos}`
                }));
            setNinos(ninosActivos);
            setNinosFiltrados(ninosActivos);
        } catch (error) {
            console.error('Error al cargar niños:', error);
            setError('Error al cargar la lista de niños');
        }
    };

    const handleBusqueda = (e) => {
        const valor = e.target.value;
        setBusqueda(valor);
        
        if (valor.trim() === '') {
            setNinosFiltrados(ninos);
        } else {
            const filtrados = ninos.filter(nino => 
                nino.nombre.toLowerCase().includes(valor.toLowerCase())
            );
            setNinosFiltrados(filtrados);
        }
    };

    const validarArchivo = (file) => {
        setArchivoError('');
        
        // Validar tipo de archivo
        const tiposPermitidos = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!tiposPermitidos.includes(file.type)) {
            setArchivoError('Solo se permiten archivos PDF e imágenes (JPEG, PNG, GIF, WEBP)');
            return false;
        }

        // Validar tamaño (10MB = 10 * 1024 * 1024 bytes)
        const tamañoMaximo = 10 * 1024 * 1024;
        if (file.size > tamañoMaximo) {
            setArchivoError('El archivo no debe superar los 10MB');
            return false;
        }

        return true;
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'archivo') {
            const file = files[0];
            if (file) {
                if (validarArchivo(file)) {
                    setFormData(prev => ({
                        ...prev,
                        [name]: file
                    }));
                    setNombreArchivo(file.name);
                } else {
                    e.target.value = ''; // Limpiar el input de archivo
                }
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        if (name === 'titulo') setTituloError('');
        if (name === 'id_child') setParticipantesError('');
        if (name === 'fecha_limite') setFechaError('');
    };

    const handleNinoSelect = (nino) => {
        if (actividad) {
            // Si estamos editando, solo permitir un estudiante
            setFormData(prev => ({ ...prev, id_child: [nino.id_child] }));
            setBusqueda(nino.nombre);
            setShowOptions(false);
            setParticipantesError('');
        } else {
            // Si estamos creando, permitir múltiples estudiantes
            const newSelected = formData.id_child.includes(nino.id_child)
                ? formData.id_child.filter(id => id !== nino.id_child)
                : [...formData.id_child, nino.id_child];
            setFormData(prev => ({ ...prev, id_child: newSelected }));
            setParticipantesError('');
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file) {
            if (validarArchivo(file)) {
                setFormData(prev => ({
                    ...prev,
                    archivo: file
                }));
                setNombreArchivo(file.name);
            }
        }
    };

    const validarFormulario = () => {
        let isValid = true;

        if (!formData.titulo.trim()) {
            setTituloError('El título es requerido');
            isValid = false;
        }

        if (formData.id_child.length === 0) {
            setParticipantesError('Debe seleccionar al menos un estudiante');
            isValid = false;
        }

        if (!formData.fecha_limite) {
            setFechaError('La fecha límite es requerida');
            isValid = false;
        } else {
            const fechaSeleccionada = new Date(formData.fecha_limite + 'T00:00:00');
            const fechaActual = new Date();
            fechaActual.setHours(0, 0, 0, 0);
            
            if (fechaSeleccionada.getTime() < fechaActual.getTime()) {
                setFechaError('La fecha no puede ser anterior a la fecha actual');
                isValid = false;
            }
        }

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validarFormulario()) return;

        setLoading(true);
        setError(null);

        try {
            const userString = localStorage.getItem('user');
            if (!userString) {
                throw new Error('No se encontró información del usuario');
            }
            
            const user = JSON.parse(userString);

            if (actividad) {
                // Si estamos editando, solo actualizar la actividad actual
            const datosActividad = {
                titulo: formData.titulo,
                descripcion: formData.descripcion,
                    id_child: formData.id_child[0], // Usar el primer (y único) id_child
                fecha_limite: formData.fecha_limite,
                id_profesor: user.id
            };

            if (formData.archivo) {
                datosActividad.archivo = formData.archivo;
                } else if (actividad.archivo && !archivoExistente) {
                datosActividad.borrarArchivo = true;
            }

                const response = await actualizarActividadExtracurricular(actividad.id_actividad, datosActividad);
                onSuccess(response);
            } else {
                // Si estamos creando, crear una actividad para cada niño seleccionado
                for (const idChild of formData.id_child) {
                    const datosActividad = {
                        titulo: formData.titulo,
                        descripcion: formData.descripcion,
                        id_child: idChild,
                        fecha_limite: formData.fecha_limite,
                        id_profesor: user.id
                    };

                    if (formData.archivo) {
                        datosActividad.archivo = formData.archivo;
                    }

                    await crearActividadExtracurricular(datosActividad);
                }
                onSuccess({ message: 'Actividades creadas exitosamente' });
            }
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const eliminarArchivoExistente = () => {
        setArchivoExistente(null);
        setNombreArchivo('');
        setFormData(prev => ({
            ...prev,
            archivo: null
        }));
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-[480px] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                        {actividad ? 'Editar Actividad' : 'Nueva Actividad'}
                    </h2>
                    <button 
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {error && (
                    <div className="mx-4 sm:mx-6 mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto">
                    <div>
                        <input
                            type="text"
                            name="titulo"
                            value={formData.titulo}
                            onChange={handleChange}
                            placeholder="Título de la actividad"
                            className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white border text-gray-700 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base ${
                                tituloError ? 'border-red-500' : ''
                            }`}
                        />
                        {tituloError && <p className="text-red-500 text-xs mt-1">{tituloError}</p>}
                    </div>

                    <div>
                        <textarea
                            name="descripcion"
                            value={formData.descripcion}
                            onChange={handleChange}
                            placeholder="Descripción detallada de la actividad"
                            rows="3"
                            className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white border text-gray-700 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 resize-none text-sm sm:text-base"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Seleccionar Estudiante
                        </label>
                        <div className="relative" ref={selectRef}>
                            <div className={`relative w-full ${participantesError ? 'border-red-500' : 'border-gray-300'}`}>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={busqueda}
                                        onChange={handleBusqueda}
                                        onFocus={() => setShowOptions(true)}
                                        placeholder="Buscar y seleccionar estudiante..."
                                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 pl-9 sm:pl-10 bg-white border text-gray-700 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                                    />
                                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                </div>
                                {showOptions && ninosFiltrados.length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 sm:max-h-60 overflow-auto">
                                        {ninosFiltrados.map(nino => (
                                            <label
                                                key={nino.id_child}
                                                className="flex items-center space-x-3 px-3 sm:px-4 py-2 hover:bg-indigo-50 cursor-pointer"
                                            >
                                                {actividad ? (
                                                    <div className="relative">
                                                        <input
                                                            type="radio"
                                                            checked={formData.id_child.includes(nino.id_child)}
                                                            onChange={() => handleNinoSelect(nino)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-4 sm:w-5 h-4 sm:h-5 border-2 border-gray-300 rounded-full transition-all peer-checked:bg-indigo-600 peer-checked:border-indigo-600 flex items-center justify-center">
                                                            <span className="text-white text-xs sm:text-sm">{formData.id_child.includes(nino.id_child) ? '●' : ''}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="relative">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.id_child.includes(nino.id_child)}
                                                            onChange={() => handleNinoSelect(nino)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-4 sm:w-5 h-4 sm:h-5 border-2 border-gray-300 rounded transition-all peer-checked:bg-indigo-600 peer-checked:border-indigo-600 flex items-center justify-center">
                                                            <span className="text-white text-xs sm:text-sm">{formData.id_child.includes(nino.id_child) ? '✓' : ''}</span>
                                                        </div>
                                                    </div>
                                                )}
                                                <span className="text-sm sm:text-base text-gray-700">{nino.nombre}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {participantesError && (
                                <p className="mt-1 text-xs sm:text-sm text-red-500">
                                    {participantesError}
                                </p>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="relative">
                            <input
                                type="date"
                                name="fecha_limite"
                                value={formData.fecha_limite}
                                onChange={handleChange}
                                min={new Date(new Date().setHours(0, 0, 0, 0)).toISOString().split('T')[0]}
                                className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 pl-9 sm:pl-10 bg-white border text-gray-700 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base ${
                                    fechaError ? 'border-red-500' : ''
                                }`}
                            />
                            <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                        {fechaError && <p className="text-red-500 text-xs sm:text-sm mt-1">{fechaError}</p>}
                    </div>

                    <div className="space-y-3">
                        {(archivoExistente || nombreArchivo) ? (
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200">
                                <div className="flex items-center space-x-3">
                                    <FaFile className="text-indigo-500 text-base sm:text-lg" />
                                    <div>
                                        <p className="text-sm sm:text-base font-medium text-gray-700">{nombreArchivo}</p>
                                        {archivoExistente && (
                                            <a 
                                                href={archivoExistente}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs sm:text-sm text-indigo-600 hover:text-indigo-800"
                                            >
                                                Ver archivo
                                            </a>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={eliminarArchivoExistente}
                                    className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    <FaTrash size={14} />
                                </button>
                            </div>
                        ) : (
                            <div>
                                <div 
                                    className={`relative ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50'}`}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                >
                                    <input
                                        type="file"
                                        name="archivo"
                                        onChange={handleChange}
                                        className="hidden"
                                        id="archivo"
                                        accept=".pdf,image/*"
                                    />
                                    <label
                                        htmlFor="archivo"
                                        className="flex flex-col items-center justify-center space-y-2 w-full px-4 py-5 sm:py-6 border-2 border-dashed rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                                    >
                                        <FaUpload className="text-gray-500 text-xl sm:text-2xl" />
                                        <div className="text-center">
                                            <p className="text-gray-600 text-sm sm:text-base">Arrastra y suelta archivos aquí o</p>
                                            <p className="text-indigo-600 font-medium text-sm sm:text-base">Haz clic para seleccionar</p>
                                            <p className="text-xs sm:text-sm text-gray-500 mt-1">Solo PDF e imágenes (máx. 10MB)</p>
                                        </div>
                                    </label>
                                </div>
                                {archivoError && (
                                    <p className="text-red-500 text-xs sm:text-sm mt-2">{archivoError}</p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors duration-200 font-medium text-sm sm:text-base"
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors duration-200 font-medium text-sm sm:text-base flex items-center justify-center min-w-[100px]"
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : 'Guardar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ModalActividadExtracurricular;
